import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CompanyApi from "../../api/companyApi";
import { ReactComponent as ChevronLeftIcon } from "../../assets/Specialists/chevronLeft.svg";
import CompaniesTable from "../../components/CompaniesTable/CompaniesTable";
import Input from "../../components/Input/Input";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import StandartButton from "../../components/StandartButton/StandartButton";
import { Company } from "../../types/Company";
import s from "./Companies.module.css";
import CreateCompany from "./CreateCompany/CreateCompany";
import SelectedCompany, {
  SelectedCompanyTab,
} from "./SelectedCompany/SelectedCompany";

const Companies = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");
  const tabFromUrl: SelectedCompanyTab =
    searchParams.get("tab") === "projects" ? "projects" : "participants";
  const [companiesData, setCompaniesData] = useState<Company[] | null>(null);
  const [page, setPage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [createCompanyModal, setCreateCompanyModal] = useState(false);
  const [searchCompany, setSearchCompany] = useState("");
  const [actionLaoders, setActionLaoders] = useState({ loadCompanies: false });
  const [reloadCompanyTriger, setReloadCompanyTriger] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  useEffect(() => {
    const makeAsync = async () => {
      if (!token) return;
      setActionLaoders((prev) => ({ ...prev, loadCompanies: true }));

      const response = await CompanyApi.getAllCompanies(
        token,
        page,
        rowsPerPage,
        searchCompany,
      );
      setActionLaoders((prev) => ({ ...prev, loadCompanies: false }));
      if (response.status && response.companies) {
        setCompaniesData(response.companies);
        setTotalAmount(response.totalAmount);
      }
    };
    makeAsync();
  }, [token, page, rowsPerPage, reloadCompanyTriger, searchCompany]);

  useEffect(() => {
    if (!token || !companyIdFromUrl || !companiesData) return;

    if (selectedCompany?._id === companyIdFromUrl) return;

    const companyFromCurrentPage = companiesData.find(
      (item) => item._id === companyIdFromUrl,
    );

    if (companyFromCurrentPage) {
      setSelectedCompany(companyFromCurrentPage);
      return;
    }

    let ignore = false;

    const loadCompanyById = async () => {
      try {
        setActionLaoders((prev) => ({ ...prev, loadCompanies: true }));
        const response = await CompanyApi.getCompanyById(
          token,
          companyIdFromUrl,
        );
        if (ignore) return;

        const companyResponse =
          response && "company" in response
            ? (response as { company: Company }).company
            : response;

        if (companyResponse) {
          setSelectedCompany(companyResponse);
          setCompaniesData((prev) => {
            if (!prev) return [companyResponse];
            if (prev.some((item) => item._id === companyResponse._id)) {
              return prev;
            }
            return [companyResponse, ...prev];
          });
        }
      } finally {
        if (!ignore) {
          setActionLaoders((prev) => ({ ...prev, loadCompanies: false }));
        }
      }
    };

    loadCompanyById();

    return () => {
      ignore = true;
    };
  }, [token, companyIdFromUrl, companiesData, selectedCompany?._id]);

  const selectCompanyHandler = (companyId: string) => {
    if (!companiesData) return;
    const selectCompany = companiesData.filter(
      (item) => item._id === companyId,
    )[0];
    setSelectedCompany(selectCompany);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("companyId", companyId);
    nextSearchParams.delete("tab");
    nextSearchParams.delete("programId");
    setSearchParams(nextSearchParams);
  };

  const backToCompaniesHandler = () => {
    setSelectedCompany(null);
    if (
      companyIdFromUrl ||
      searchParams.get("tab") ||
      searchParams.get("programId")
    ) {
      navigate("/companies", { replace: true });
    }
  };

  return (
    <div className={s.container}>
      <div
        className={`${s.allCompaniesContainer} ${
          selectedCompany ? s.allCompaniesHidden : s.allCompaniesVisible
        }`}
      >
        <div className={s.headBlock}>
          <div className={s.searchBlock}>
            <Input
              inputValue={searchCompany}
              isVisible
              onChangeInput={(value) => setSearchCompany(value)}
              required
              placeholder="Search company by title..."
              isSearchinput
            />
          </div>
          <StandartButton
            buttonTitle="Create Company"
            action={() => setCreateCompanyModal(true)}
          />
        </div>
        {actionLaoders.loadCompanies ? (
          <div className={s.mainLoaderContainer}>
            <Loader size={100} />
          </div>
        ) : (
          <div className={s.tableBlock}>
            {companiesData && companiesData.length ? (
              <CompaniesTable
                count={totalAmount}
                page={page}
                rowsPerPage={rowsPerPage}
                selectCompaniesHandler={selectCompanyHandler}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                rows={companiesData}
                setReloadCompanyTriger={setReloadCompanyTriger}
              />
            ) : null}
          </div>
        )}
      </div>
      <div
        className={`${s.selectedCompanyInfo} ${
          selectedCompany ? s.companyInfoVisible : s.companyInfoHidden
        }`}
      >
        {selectedCompany && (
          <div className={s.companyInfoContentBlock}>
            <div className={s.locationBlock}>
              <div
                className={s.backBtn}
                role="button"
                onClick={backToCompaniesHandler}
              >
                <ChevronLeftIcon className={s.backIcon} />
                <span>Back</span>
              </div>
              <div className={s.locationTitle}>
                <span>companies</span>
                {`>`}
                <span>{selectedCompany.name}</span>
              </div>
            </div>
            <div className={s.contentBlock}>
              <SelectedCompany
                selectedCompanyData={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                setCompaniesData={setCompaniesData}
                initialActiveTab={tabFromUrl}
              />
            </div>
          </div>
        )}
      </div>
      <ModalWindow
        setIsOpen={setCreateCompanyModal}
        isOpen={createCompanyModal}
        maxWidth="fit-content"
      >
        <CreateCompany
          setModalVisible={setCreateCompanyModal}
          setPage={setPage}
          setReloadCompanyTriger={setReloadCompanyTriger}
        />
      </ModalWindow>
    </div>
  );
};

export default Companies;
